import type { ForwardedRef, RefObject } from 'react';
import { useRef } from 'react';
import { useEffectOnValueChange } from './useEffectOnValueChange';

/**
 * Wraps zero or more refs in a facade to ensure there's always a ref available, even if the parent
 * component doesn't provide any forwarded refs.
 * @param refs Forwarded refs to wrap.
 * @returns Ref facade.
 */
export const useRefFacade = <T>(...refs: Array<ForwardedRef<T> | undefined>): RefObject<T> => {
  const refFacade = useRef<T>(null);

  useEffectOnValueChange(
    refs,
    () => {
      if (!refs) return;

      for (const ref of refs) {
        if (!ref) continue;

        if (typeof ref === 'function') {
          ref(refFacade.current ?? null);
        } else {
          ref.current = refFacade.current ?? null;
        }
      }
    },
    true
  );

  return refFacade;
};
